import {
  DatepickerV2,
  DD,
  DT,
  FormField,
  GridCol,
  GridRow,
  HelperText,
  IntervalOption,
  isDefinedString,
  isValidDefinedDate,
  isValidDefinedInterval,
  Select,
  toIntervalString,
  toUTCDate,
  toZonedDateStringOrUndefined,
  Typography,
  useIntervalOption,
} from '@client/toolkit';
import { useTranslation } from 'react-i18next';
import { FieldErrors, UseFormSetValue } from 'react-hook-form';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { FormValues, FormPath } from '../agreement-conditions-card.component';
import { IntervalType } from '@client/api';

const INTERVAL_OPTIONS: IntervalOption[] = ['Monthly', 'EveryTwoMonth', 'EveryThreeMonth', 'EveryFourMonth', 'EveryFiveMonth', 'EverySixMonth', 'Yearly'];

interface InvoicingPeriodProps {
  isFetching: boolean;
  editing: boolean;
  setValue: UseFormSetValue<FormValues>;
  errors: FieldErrors<FormValues>;
  invoicingStart?: string | null;
  invoicingPeriod?: { type?: IntervalType | null; count?: number | null } | null;
  isSubmitting: boolean;
}

export const InvoicingPeriod = ({
  isFetching,
  editing,
  setValue,
  errors,
  invoicingStart: invoicingStartString,
  invoicingPeriod,
  isSubmitting,
}: InvoicingPeriodProps) => {
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');
  const { t: tValidation } = useTranslation('validation');

  const invoicingStart = useMemo(
    () =>
      invoicingStartString != null && isDefinedString(invoicingStartString)
        ? toUTCDate(invoicingStartString)
        : undefined,
    [invoicingStartString],
  );

  const { intervalOption: invoicingIntervalOption, updateIntervalOption: updateInvoicingIntervalOption } =
    useIntervalOption({
      interval: editing ? (invoicingPeriod as any) : undefined,
      onUpdateIntervalOption: ({ intervalCount, intervalType }) => {
        setValue<FormPath>('invoicingPeriod.count', intervalCount || ('' as any), {
          shouldDirty: true,
          shouldTouch: false,
          shouldValidate: true,
        });
        setValue<FormPath>('invoicingPeriod.type', intervalType || ('' as any), {
          shouldDirty: true,
          shouldTouch: false,
          shouldValidate: true,
        });
      },
    });

  const invoicingIntervalErrorMessage: string | undefined = useMemo(() => {
    if (errors?.invoicingPeriod == null) return;
    return tValidation('field.required');
  }, [errors?.invoicingPeriod, tValidation]);

  const hasInvoicingIntervalError = useMemo(
    () => !!invoicingIntervalErrorMessage && invoicingIntervalOption !== 'Custom',
    [invoicingIntervalErrorMessage, invoicingIntervalOption],
  );

  return (
    <div className="py-2" data-testid="invoicing-period">
      <Typography variant="none" className="mb-4 text-brand-primary font-medium uppercase text-xs">
        {tGlobal('agreement.invoicingPeriod')}
      </Typography>

      <GridRow cols={3} spacing={2} className="items-center">
        <GridCol span={1}>
          <DT>{tGlobal('agreement.invoicingStart')}</DT>
        </GridCol>
        <GridCol span={2} loading={isFetching}>
          {!editing ? (
            <DD isEmpty={!isValidDefinedDate(invoicingStart)}>{toZonedDateStringOrUndefined(invoicingStart)}</DD>
          ) : (
            <FormField<FormValues, FormPath>
              name="invoicingStart"
              render={({ field: { value, ...field } }) => (
                <DatepickerV2
                  value={value ? dayjs(value) : undefined}
                  {...field}
                  onChange={(date) => {
                    field.onChange(date?.format('YYYY-MM-DD'));
                  }}
                  className="w-full"
                />
              )}
            />
          )}
        </GridCol>

        <GridCol span={1}>
          <DT>{tGlobal('agreement.invoicingPeriod', { context: 'short' })}</DT>
        </GridCol>
        <GridCol span={2} loading={isFetching}>
          {!editing ? (
            <DD isEmpty={!isValidDefinedInterval(invoicingPeriod as any)}>
              {toIntervalString(invoicingPeriod as any)}
            </DD>
          ) : (
            <>
              <Select
                size="medium"
                value={invoicingIntervalOption ?? ''}
                onChange={(e) => updateInvoicingIntervalOption(e.target.value as IntervalOption)}
                error={hasInvoicingIntervalError}
                submitting={isSubmitting}
              >
                {INTERVAL_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {tType(`IntervalOption.${option}`)}
                  </option>
                ))}
              </Select>
              {hasInvoicingIntervalError && (
                <HelperText className="mt-1" error={hasInvoicingIntervalError}>
                  {invoicingIntervalErrorMessage}
                </HelperText>
              )}
            </>
          )}
        </GridCol>
      </GridRow>
    </div>
  );
};
